import i18n from '@/assets/i18n/i18n'
import usageApi, { saveDownload } from '@/api/usageApi'
import { router } from '@/router.js'

// The list's column values → the API's sort keys
const SORTS = {
    fileName: 'file',
    dataType: 'dataType',
    source: 'source',
    usageArea: 'usageArea',
    period: 'period',
    importedAt: 'imported',
    importedBy: 'importedBy'
}
const POLL_MS = 2000

// The Usage API's error codes that already have a language constant; any other code shows the API's own message
const ERROR_CODE_KEYS = {
    busy: 'lang_GENERIC_SNACKBAR_ERROR_ANOTHER_PROCESS_RUNNING',
    duplicate_dataset: 'lang_GENERIC_MESSAGE_ERROR_FILE_ALREADY_UPLOADED',
    nothing_to_export: 'lang_ERROR_NOTHING_TO_EXPORT',
    no_export_mapping: 'lang_GENERIC_MESSAGE_WARNING_NO_MAPPINGS_AVAILABLE_FOR_DOMAIN',
    usage_error: 'lang_GENERIC_MESSAGE_ERROR_UNKNOWN'
}

/** The Usage API names every error with a code; a known one has its own text, any other shows the API's message. */
export function usageErrorText(error) {
    const key = ERROR_CODE_KEYS[error.code]
    return key ? i18n.t(key) : (error.message || i18n.t('lang_GENERIC_MESSAGE_ERROR_UNKNOWN'))
}

const state = {
// List
    usage: [],
    selectedUsage: [],
    isAllSelectedUsage: false,
    expandedUsage: [],
    usageDetails: [],
    usageServerSideSortingOptions: { sortBy: ['importedAt'], sortDesc: [true] },

// Filters
    selectedUsageAreaFilter: null,
    selectedUsageDataTypeFilter: null,
    selectedUsageSourceFilter: null,
    selectedUsagePeriodFilter: null,

// Actions
    selectedUsageAction: [],
    selectedUsageImport: null,
    showUsageImportDialog: false,

// Compare Usage: { show, dataset, phase: confirm|starting|running|done|nothing|failed, message, statements, comparisonId, percent,
//                  noSheet: { fromStatement, airingLevel } }
    compareUsageDialog: { show: false },
    compareUsagePolling: null,

// Delete: { show, datasets: [detail], deleting }
    usageDeleteDialog: { show: false, datasets: [], deleting: false },

// GEMA's station list, loaded once for the station pickers
    usageStationList: []
}

const getters = {
    getUsage                                (state) { return state.usage },
    getSelectedUsage                        (state) { return state.selectedUsage },
    getIsAllSelectedUsage                   (state) { return state.isAllSelectedUsage },
    getExpandedUsage                        (state) { return state.expandedUsage },
    getUsageDetails                         (state) { return state.usageDetails },
    getUsageServerSideSortingOptions        (state) { return state.usageServerSideSortingOptions },
    getSelectedUsageAreaFilter              (state) { return state.selectedUsageAreaFilter },
    getSelectedUsageDataTypeFilter          (state) { return state.selectedUsageDataTypeFilter },
    getSelectedUsageSourceFilter            (state) { return state.selectedUsageSourceFilter },
    getSelectedUsagePeriodFilter            (state) { return state.selectedUsagePeriodFilter },
    getSelectedUsageAction                  (state) { return state.selectedUsageAction },
    getSelectedUsageImport                  (state) { return state.selectedUsageImport },
    getShowUsageImportDialog                (state) { return state.showUsageImportDialog },
    getCompareUsageDialog                   (state) { return state.compareUsageDialog },
    getUsageDeleteDialog                    (state) { return state.usageDeleteDialog },
    getUsageStationList                     (state) { return state.usageStationList },
    getSelectedUsageRows                    (state) { return state.usage.filter(row => state.selectedUsage.includes(row.alvId)) }
}

const mutations = {
    setUsage:                               (state, rows) => { state.usage = rows },
    setSelectedUsage:                       (state, ids) => { state.selectedUsage = ids },
    setIsAllSelectedUsage:                  (state, value) => { state.isAllSelectedUsage = value },
    setExpandedUsage:                       (state, items) => { state.expandedUsage = items },
    setUsageDetails:                        (state, details) => { state.usageDetails = details },
    setUsageServerSideSortingOptions:       (state, options) => { state.usageServerSideSortingOptions = options },
    setSelectedUsageAreaFilter:             (state, value) => { state.selectedUsageAreaFilter = value },
    setSelectedUsageDataTypeFilter:         (state, value) => { state.selectedUsageDataTypeFilter = value },
    setSelectedUsageSourceFilter:           (state, value) => { state.selectedUsageSourceFilter = value },
    setSelectedUsagePeriodFilter:           (state, value) => { state.selectedUsagePeriodFilter = value },
    setSelectedUsageAction:                 (state, value) => { state.selectedUsageAction = value },
    setSelectedUsageImport:                 (state, value) => { state.selectedUsageImport = value },
    setShowUsageImportDialog:               (state, show) => { state.showUsageImportDialog = show },
    setCompareUsageDialog:                  (state, dialog) => { state.compareUsageDialog = dialog },
    setCompareUsagePolling:                 (state, polling) => { state.compareUsagePolling = polling },
    setUsageDeleteDialog:                   (state, dialog) => { state.usageDeleteDialog = dialog },
    setUsageStationList:                    (state, stations) => { state.usageStationList = stations },
    // A dataset's detail, refreshed in place while its row is open
    replaceUsageDetails:                    (state, detail) => {
        state.usageDetails = state.usageDetails.map(d => d.id === detail.id ? detail : d)
        state.expandedUsage = state.expandedUsage.map(d => d.id === detail.id ? detail : d)
    }
}

const actions = {
// LIST
    async fetchUsage({ commit, getters }) {
        commit('setIsLoading', true)
        const sort = getters.getUsageServerSideSortingOptions || {}
        const sortBy = sort.sortBy && sort.sortBy[0] ? SORTS[sort.sortBy[0]] : null
        const result = await usageApi.list({
            usageArea: getters.getSelectedUsageAreaFilter,
            dataType: getters.getSelectedUsageDataTypeFilter,
            source: getters.getSelectedUsageSourceFilter,
            period: getters.getSelectedUsagePeriodFilter,
            sort: sortBy,
            direction: sortBy ? (sort.sortDesc && sort.sortDesc[0] ? 'desc' : 'asc') : null,
            page: getters.getCurrentPage,
            perPage: getters.getItemsPerPage
        })
        if (!result.ok) {
            commit('setUsage', [])
            commit('setNoDataText', 'lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')
            commit('setSnackbar', { type: 'error', msg: usageErrorText(result.error) })
            commit('setIsLoading', false)
            return
        }
        // The list components select and expand by alvId and id; a dataset's id serves as both
        commit('setUsage', result.data.rows.map(row => ({ ...row, alvId: row.id })))
        if (result.data.rows.length === 0) commit('setNoDataText', 'lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')
        this._vm.$alvPlugin.storePagingInformations(result.data.total, result.data.page, result.data.total)
        commit('setIsLoading', false)
    },

    async fetchUsageDetails({ commit }, id) {
        const result = await usageApi.detail(id)
        commit('setIsLoading', false)
        if (!result.ok) {
            commit('setSnackbar', { type: 'error', msg: usageErrorText(result.error) })
            commit('setUsageDetails', [])
            return
        }
        commit('setUsageDetails', [{ ...result.data, alvId: result.data.id }])
    },

    async fetchUsageStationList({ commit, getters }) {
        if (getters.getUsageStationList.length > 0) return
        const result = await usageApi.stationList('GEMA')
        if (result.ok) commit('setUsageStationList', result.data.stations)
    },

// ACTIONS dropdown
    doUsageAction({ commit, dispatch, getters }) {
        const rows = getters.getSelectedUsageRows
        const needs = (text) => {
            commit('setSelectionRequiredText', text)
            commit('setShowSelectionRequiredDialog', true)
        }
        switch (getters.getSelectedUsageAction) {
            case 'compare':
                if (rows.length !== 1 || rows[0].dataType !== 'broadcast_schedule') return needs('lang_GENERAL_SELECTION_REQUIRED_DIALOG_TEXT_ONLY_ONE')
                if (!rows[0].comparable) return needs('lang_PUBLISHING_USAGE_MESSAGE_WARNING_SELECTION_COMPARE_NOT_IMPORTED')
                return dispatch('openCompareUsage', rows[0])
            case 'review':
                if (rows.length !== 1 || rows[0].dataType !== 'broadcast_schedule') return needs('lang_GENERAL_SELECTION_REQUIRED_DIALOG_TEXT_ONLY_ONE')
                commit('setSelectedUsageAction', [])
                return router.push({ name: 'UsageReview', params: { alvId: rows[0].id } })
            case 'download':
                if (rows.length === 0) return needs('lang_GENERAL_SELECTION_REQUIRED_DIALOG_TEXT_AT_LEAST_ONE')
                return Promise.all(rows.map(row => dispatch('downloadUsageFile', row)))
            case 'delete':
                if (rows.length === 0) return needs('lang_GENERAL_SELECTION_REQUIRED_DIALOG_TEXT_AT_LEAST_ONE')
                return dispatch('openUsageDelete', rows)
        }
    },

    async downloadUsageFile({ commit }, row) {
        const result = await usageApi.download(row.id)
        if (!result.ok) return commit('setSnackbar', { type: 'error', msg: usageErrorText(result.error) })
        saveDownload(result, row.fileName)
    },

// IMPORT dropdown: the chosen data type opens the import dialog
    doUsageImport({ commit }) {
        commit('setShowUsageImportDialog', true)
    },

// COMPARE USAGE
    async openCompareUsage({ commit, getters }, dataset) {
        commit('setCompareUsageDialog', {
            show: true, dataset, phase: 'confirm', message: '', statements: [], comparisonId: null, percent: 0,
            noSheet: { fromStatement: false, airingLevel: false }
        })
        // Offer what the last comparison of this schedule did; the dialog is already open, so a slow answer does not hold it up
        const detail = await usageApi.detail(dataset.id)
        const previous = detail.ok && detail.data.comparison && detail.data.comparison.settings ? detail.data.comparison.settings.noSheet : null
        const dialog = getters.getCompareUsageDialog
        if (previous && dialog.show && dialog.phase === 'confirm' && dialog.dataset.id === dataset.id) {
            commit('setCompareUsageDialog', { ...dialog, noSheet: { fromStatement: !!previous.fromStatement, airingLevel: !!previous.airingLevel } })
        }
    },

    setCompareUsageNoSheet({ commit, getters }, choice) {
        const dialog = getters.getCompareUsageDialog
        commit('setCompareUsageDialog', { ...dialog, noSheet: { ...dialog.noSheet, ...choice } })
    },

    async startCompareUsage({ commit, dispatch, getters }) {
        const dialog = getters.getCompareUsageDialog
        commit('setCompareUsageDialog', { ...dialog, phase: 'starting' })
        const result = await usageApi.compare(dialog.dataset.id, dialog.noSheet)
        if (!result.ok) {
            return commit('setCompareUsageDialog', { ...dialog, phase: 'failed', message: usageErrorText(result.error) })
        }
        if (result.data.status === 'nothing_to_compare') {
            return commit('setCompareUsageDialog', { ...dialog, phase: 'nothing', message: result.data.message })
        }
        commit('setCompareUsageDialog', {
            ...dialog,
            phase: 'running',
            statements: result.data.statements || [],
            comparisonId: result.data.comparisonId,
            processId: result.data.processId,
            percent: 0
        })
        dispatch('pollCompareUsage')
    },

    pollCompareUsage({ commit, dispatch, getters, state }) {
        clearInterval(state.compareUsagePolling)
        const polling = setInterval(async () => {
            const dialog = getters.getCompareUsageDialog
            if (!dialog.show || dialog.phase !== 'running') return clearInterval(polling)
            const progress = await usageApi.progress(dialog.processId)
            if (progress && typeof progress.percent === 'number') {
                commit('setCompareUsageDialog', { ...getters.getCompareUsageDialog, percent: progress.percent })
            }
            // The comparison's own status is the outcome; the process only says how far it got
            const detail = await usageApi.detail(dialog.dataset.id)
            const comparison = detail.ok ? detail.data.comparison : null
            if (!comparison || ['queued', 'running'].includes(comparison.status)) return
            clearInterval(polling)
            commit('setCompareUsagePolling', null)
            commit('setCompareUsageDialog', {
                ...getters.getCompareUsageDialog,
                phase: comparison.status === 'completed' ? 'done' : 'failed',
                percent: 100,
                summary: comparison.summary,
                message: comparison.error || ''
            })
            commit('replaceUsageDetails', { ...detail.data, alvId: detail.data.id })
            dispatch('fetchUsage')
        }, POLL_MS)
        commit('setCompareUsagePolling', polling)
    },

    closeCompareUsage({ commit, state }) {
        // Closing leaves the comparison running; the row shows its state and the review opens when it is done
        clearInterval(state.compareUsagePolling)
        commit('setCompareUsagePolling', null)
        commit('setCompareUsageDialog', { show: false })
        commit('setSelectedUsageAction', [])
    },

// DELETE
    async openUsageDelete({ commit }, rows) {
        commit('setUsageDeleteDialog', { show: true, datasets: [], deleting: false, loading: true })
        const details = await Promise.all(rows.map(row => usageApi.detail(row.id)))
        commit('setUsageDeleteDialog', {
            show: true,
            deleting: false,
            loading: false,
            datasets: details.filter(d => d.ok).map(d => d.data)
        })
    },

    async deleteUsageDatasets({ commit, dispatch, getters }) {
        const dialog = getters.getUsageDeleteDialog
        commit('setUsageDeleteDialog', { ...dialog, deleting: true })
        const deletable = dialog.datasets.filter(d => d.deletable)
        const results = await Promise.all(deletable.map(d => usageApi.remove(d.id)))
        const failed = results.filter(r => !r.ok)
        if (failed.length > 0) {
            commit('setSnackbar', { type: 'error', msg: usageErrorText(failed[0].error) })
        } else {
            commit('setSnackbar', { type: 'success', msg: i18n.t('lang_GENERIC_SNACKBAR_ENTRY_DELETED') })
        }
        commit('setUsageDeleteDialog', { show: false, datasets: [], deleting: false })
        commit('setSelectedUsage', [])
        commit('setIsAllSelectedUsage', false)
        commit('setSelectedUsageAction', [])
        dispatch('fetchUsage')
    }
}

export default {
    state,
    getters,
    mutations,
    actions
}
